import { apiClient } from 'axios-client';
import { useProReviewInvitationsFormCreateQuery } from 'generated/graphql';
import React, { FunctionComponent, useCallback, useMemo } from 'react';

import { useCreateReviewInvitation } from 'generated/api-client/@tanstack/react-query.gen';
import { ProReviewInvitationsFormCreateContainer } from './pro-review-invitations-form-create-container';
import { ProReviewInvitation } from './pro-review-invitations-form-fields.type';

type Props = {
  proPresentationId: number;
  proViewId?: number;
};

export type ProReviewInvitationsFormCreateRepositoryProReview = {
  id: number;
  dateCreated: string;
  givenName: string;
  familyName: string;
  email: string;
}[];

export type ProReviewInvitationsFormCreateRepositoryProReviewInvitation = {
  id: number;
  dateCreated: string;
  givenName: string;
  familyName: string;
  email: string;
  proPresentation?: {
    id: number;
    slug: string;
    mainTrade?: { slug: string };
    proViews: {
      id: number;
    }[];
  };
}[];

export const ProReviewInvitationsFormCreateRepository: FunctionComponent<
  Props
> = ({ proPresentationId, proViewId }) => {
  const { data, loading } = useProReviewInvitationsFormCreateQuery({
    variables: {
      proPresentationId,
    },
  });

  // Concat all proPresentation reviews with all others reviews through companyId
  const proReviews: ProReviewInvitationsFormCreateRepositoryProReview = useMemo(
    () =>
      [
        ...(data?.proPresentationByPk?.proReviews ?? []),
        ...(data?.proPresentationByPk?.company?.proPresentations ?? []).flatMap(
          (proPresentation) => proPresentation.proReviews,
        ),
      ].map(({ review }) => review),
    [data],
  );

  //Concat all proReviewInvitations with all other reviews through companyId
  const proReviewInvitations: ProReviewInvitationsFormCreateRepositoryProReviewInvitation =
    useMemo(
      () => [
        ...(data?.proPresentationByPk?.proReviewInvitations ?? []),
        ...(data?.proPresentationByPk?.company?.proPresentations ?? []).flatMap(
          (proPresentation) => proPresentation.proReviewInvitations,
        ),
      ],
      [data],
    );

  const { mutateAsync: createReviewInvitation } = useCreateReviewInvitation();

  const createProReviewInvitations = useCallback(
    async (proReviewInvitations: ProReviewInvitation[]) => {
      return Promise.all(
        proReviewInvitations.map(async (proReviewInvitation) => {
          const {
            proof,
            dateOfWork,
            proPresentationId: _dropped,
            ...rest
          } = proReviewInvitation;
          void _dropped;

          const jsonPayload = {
            proPresentation: proPresentationId,
            ...rest,
            ...(dateOfWork && { dateOfWork: dateOfWork.toISOString() }),
            namespace: 'bilik',
          };

          const {
            data: { reviewInvitationId },
          } = await apiClient.post<{ reviewInvitationId: number }>(
            `/form/review-invitation`,
            jsonPayload,
          );

          // V2
          // TODO: Migrate full create logic here from `/form/review-invitation` route
          // For now, it only submits the proof file
          // Once migrated : remove the loop and convert this route to batch create
          await createReviewInvitation({
            path: { reviewInvitationId },
            body: { presentationId: proPresentationId, proof },
          });
        }),
      );
    },
    [proPresentationId, createReviewInvitation],
  );

  return (
    <ProReviewInvitationsFormCreateContainer
      createProReviewInvitations={createProReviewInvitations}
      proPresentationId={proPresentationId}
      proReviews={proReviews}
      proReviewInvitations={proReviewInvitations}
      proViewId={proViewId}
      loading={loading}
    />
  );
};
